import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, CheckSquare, Users, Plus, ArrowRight, Armchair, AlertTriangle } from 'lucide-react';
import { FURNITURE_STAGES, FURNITURE_STAGE_LABELS, FURNITURE_STAGE_COLORS } from '../../components/os/FurnitureShared';

const STAGE_LABELS = {
  client_intake: 'Intake', design_proposal: 'Propuesta Diseño',
  design_phase_a: 'Diseño A', design_phase_b: 'Diseño B', design_phase_c: 'Diseño C',
  installation_proposal: 'Propuesta Montaje', procurement: 'Procurement',
  installation: 'Instalación', completed: 'Entregado',
};

const STAGE_COLORS = {
  client_intake: '#C9B8A8', design_proposal: '#C9A66B', design_phase_a: '#D4A574',
  design_phase_b: '#D4A574', design_phase_c: '#D4A574',
  installation_proposal: '#C9A66B', procurement: '#8B9F8D',
  installation: '#8B9F8D', completed: '#6B8F6B',
};

export default function OSDashboard() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [teamCount, setTeamCount] = useState(0);
  const [furnitureSummary, setFurnitureSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, tRes, fRes] = await Promise.all([
          authFetch('/api/projects/'),
          authFetch('/api/tasks/'),
          authFetch('/api/furniture/summary/dashboard'),
        ]);
        const p = await pRes.json();
        const t = await tRes.json();
        if (fRes.ok) setFurnitureSummary(await fRes.json());
        setProjects(Array.isArray(p) ? p : []);
        setTasks(Array.isArray(t) ? t : []);
        if (user?.role === 'admin') {
          const uRes = await authFetch('/api/auth/users');
          const u = await uRes.json();
          setTeamCount(Array.isArray(u) ? u.length : 0);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const activeProjects = projects.filter(p => p.status === 'active');
  const pendingTasks = tasks.filter(t => t.status === 'pending');

  const stats = [
    { label: 'Proyectos Activos', value: activeProjects.length, icon: FolderKanban, color: '#C9A66B' },
    { label: 'Tareas Pendientes', value: pendingTasks.length, icon: CheckSquare, color: '#D4A574' },
    { label: 'Equipo', value: teamCount, icon: Users, color: '#8B9F8D' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#C9A66B', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div data-testid="os-dashboard" className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>
            Bienvenido, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-sm mt-1" style={{ color: '#C9B8A8' }}>Panel de control CR HOME OS</p>
        </div>
        {(user?.role === 'admin' || user?.role === 'designer') && (
          <button
            data-testid="new-project-btn"
            onClick={() => navigate('/os/projects?new=1')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-light tracking-wide transition-all duration-300"
            style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}
            onMouseEnter={e => e.target.style.backgroundColor = '#C9A66B'}
            onMouseLeave={e => e.target.style.backgroundColor = '#2D2D2D'}
          >
            <Plus size={16} /> Nuevo Proyecto
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-xl p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
                <Icon size={20} style={{ color }} />
              </div>
            </div>
            <p className="text-3xl font-light" style={{ color: '#2D2D2D', fontFamily: 'Cormorant Garamond, serif' }}>{value}</p>
            <p className="text-xs mt-1 tracking-wide" style={{ color: '#C9B8A8' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Recent Projects */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Proyectos Recientes</h2>
          <button onClick={() => navigate('/os/projects')} className="flex items-center gap-1 text-xs tracking-wide transition-colors" style={{ color: '#C9A66B' }}>
            Ver todos <ArrowRight size={14} />
          </button>
        </div>
        {activeProjects.length === 0 ? (
          <div className="rounded-xl border p-12 text-center" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
            <FolderKanban size={40} className="mx-auto mb-4" style={{ color: '#E8E3D8' }} />
            <p className="text-sm font-light" style={{ color: '#C9B8A8' }}>No hay proyectos aún</p>
            <p className="text-xs mt-1" style={{ color: '#C9B8A8' }}>Crea tu primer proyecto para comenzar</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {activeProjects.slice(0, 6).map(p => (
              <div
                key={p.id}
                data-testid={`project-card-${p.id}`}
                onClick={() => navigate(`/os/projects/${p.id}`)}
                className="rounded-xl border p-5 cursor-pointer transition-all duration-200 hover:shadow-md"
                style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-medium truncate" style={{ color: '#2D2D2D' }}>{p.name}</h3>
                  <span className="text-[10px] px-2.5 py-1 rounded-full whitespace-nowrap font-medium"
                    style={{ backgroundColor: (STAGE_COLORS[p.stage] || '#C9B8A8') + '20', color: STAGE_COLORS[p.stage] || '#C9B8A8' }}>
                    {STAGE_LABELS[p.stage] || p.stage}
                  </span>
                </div>
                <p className="text-xs" style={{ color: '#C9B8A8' }}>{p.client_name}</p>
                {p.address && <p className="text-xs mt-1 truncate" style={{ color: '#C9B8A8' }}>{p.address}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Resumen Mobiliario a la Medida */}
      {furnitureSummary && furnitureSummary.total > 0 && (
        <div data-testid="furniture-summary">
          <h2 className="flex items-center gap-2 text-lg font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>
            <Armchair size={18} style={{ color: '#C9A66B' }} /> Mobiliario a la Medida
            <span className="text-xs font-sans" style={{ color: '#C9B8A8' }}>({furnitureSummary.total} mueble{furnitureSummary.total !== 1 ? 's' : ''})</span>
          </h2>
          <div className="rounded-xl border p-5" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
            <div className="flex flex-wrap gap-2 mb-1">
              {FURNITURE_STAGES.map(s => (
                <div key={s} data-testid={`furniture-stage-count-${s}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
                  style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', opacity: furnitureSummary.by_stage[s] ? 1 : 0.45 }}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: FURNITURE_STAGE_COLORS[s] }} />
                  <span style={{ color: '#2D2D2D' }}>{FURNITURE_STAGE_LABELS[s]}</span>
                  <span className="font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: FURNITURE_STAGE_COLORS[s] + '20', color: FURNITURE_STAGE_COLORS[s] }}>
                    {furnitureSummary.by_stage[s] || 0}
                  </span>
                </div>
              ))}
            </div>
            {furnitureSummary.alerts.length > 0 && (
              <div className="mt-4 pt-4 border-t" style={{ borderColor: '#E8E3D8' }}>
                <p className="flex items-center gap-1.5 text-xs font-medium tracking-wider uppercase mb-2" style={{ color: '#B87B7B' }}>
                  <AlertTriangle size={12} /> Alertas pendientes ({furnitureSummary.alerts.length})
                </p>
                {furnitureSummary.alerts.map(a => (
                  <button key={a.id} data-testid={`furniture-alert-row-${a.id}`}
                    onClick={() => navigate(`/os/projects/${a.project_id}/furniture/${a.id}`)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-neutral-50 transition-colors">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: '#B87B7B' }} />
                    <span className="text-sm font-light flex-1 truncate" style={{ color: '#2D2D2D' }}>
                      {a.nombre} <span className="text-xs" style={{ color: '#C9B8A8' }}>· {a.project_name}</span>
                    </span>
                    <span className="text-xs shrink-0" style={{ color: '#B87B7B' }}>{a.alert}</span>
                    <ArrowRight size={13} style={{ color: '#C9B8A8' }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending Tasks */}
      {pendingTasks.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Tareas Pendientes</h2>
            <button onClick={() => navigate('/os/tasks')} className="flex items-center gap-1 text-xs tracking-wide" style={{ color: '#C9A66B' }}>
              Ver todas <ArrowRight size={14} />
            </button>
          </div>
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
            {pendingTasks.slice(0, 5).map((t, i) => (
              <div key={t.id} className={`flex items-center gap-4 px-5 py-3 ${i > 0 ? 'border-t' : ''}`} style={{ borderColor: '#E8E3D8' }}>
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: t.priority === 'high' ? '#B87B7B' : t.priority === 'medium' ? '#D4A574' : '#8B9F8D' }} />
                <p className="text-sm font-light flex-1 truncate" style={{ color: '#2D2D2D' }}>{t.title}</p>
                {t.due_date && <span className="text-xs" style={{ color: '#C9B8A8' }}>{t.due_date}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
